/**
 * Utilidades para leer el consentimiento de cookies gestionado por CookieYes.
 *
 * CookieYes almacena las preferencias del usuario en la cookie `cookieyes-consent`
 * con un formato de pares clave:valor separados por comas. Ejemplo:
 *   consentid:xxx,consent:yes,action:yes,necessary:yes,functional:yes,
 *   analytics:no,performance:yes,advertisement:no,other:no,lastRenewedDate:...
 *
 * La categoría relevante para Facebook CAPI (fbp, fbc, email, userId) es `advertisement`.
 */

import { config } from '@/config/config';

/**
 * Parsea la cookie `cookieyes-consent` y devuelve un objeto con las categorías.
 * Devuelve un objeto vacío si la cookie no existe (usuario que todavía no ha interactuado).
 */
function parseCookieYesConsent(): Record<string, string> {
  if (typeof document === 'undefined') return {};

  const raw = document.cookie
    .split('; ')
    .find(c => c.startsWith('cookieyes-consent='))
    ?.split('=')
    .slice(1)   // todo lo que hay después del primer '='
    .join('='); // protege si el valor base64 del consentid contiene '='

  if (!raw) return {};

  try {
    // CookieYes puede URL-encodear el valor (e.g. %3A en vez de ':', %2C en vez de ',').
    const decoded = decodeURIComponent(raw);
    return Object.fromEntries(
      decoded.split(',')
        .map(pair => pair.split(':'))
        .filter(parts => parts.length === 2)
        .map(([k, v]) => [k.trim(), v.trim()])
    );
  } catch {
    return {};
  }
}

/**
 * Devuelve true si el usuario aceptó la categoría `advertisement` de CookieYes.
 *
 * Esta categoría cubre:
 *  - Cookies de Facebook Pixel (_fbp, _fbc)
 *  - Envío de datos de usuario (email, userId, phone) a Facebook CAPI
 *
 * Si la cookie todavía no existe (primera visita sin decisión), se devuelve false
 * para respetar el principio de privacy-by-default (GDPR Art. 25).
 *
 * EXCEPCIÓN: en desarrollo puede forzarse a true con `NEXT_PUBLIC_FB_CAPI_DEV_CONSENT=true`
 * (apagado por defecto — ver el porqué en el cuerpo).
 */
export function hasAdvertisementConsent(): boolean {
  // Interruptor manual SOLO de desarrollo, apagado por defecto: en local no se envía nada a
  // Facebook salvo que se pida explícitamente.
  //
  // El banner de CookieYes NO lo carga este repo: entra por Google Tag Manager (GTM-NL6VQJL) y
  // CookieYes solo lo sirve en los dominios dados de alta en su cuenta, así que en `localhost`
  // nunca aparece, la cookie `cookieyes-consent` no llega a existir y esta función devuelve
  // false — con lo que NINGÚN evento CAPI (ViewContent, InitiateCheckout, Search, Purchase)
  // llega a llamarse. Para probar el embudo, `NEXT_PUBLIC_FB_CAPI_DEV_CONSENT=true` en
  // `.env.local` (ver `config.tracking`).
  //
  // El doble candado con NODE_ENV es a propósito: en QA y producción manda siempre la cookie
  // real, aunque la variable se colase en el build.
  if (process.env.NODE_ENV === 'development' && config.tracking.facebookCapiDevConsent) return true;

  const consent = parseCookieYesConsent();
  return consent['advertisement'] === 'yes';
}

/**
 * Devuelve true si el usuario aceptó la categoría `analytics` de CookieYes.
 *
 * Es la que cubre GA4 (medición de audiencia y conversiones), y es una decisión DISTINTA de la de
 * publicidad: mucha gente acepta analítica y rechaza publicidad. Por eso los eventos de
 * `@/utils/analytics` no pueden colgar de `hasAdvertisementConsent()` — hacerlo dejaría fuera de
 * las métricas de negocio a todo ese grupo.
 *
 * Mismo criterio de privacy-by-default que la de publicidad: sin cookie (primera visita, sin
 * decisión) se devuelve false.
 *
 * EXCEPCIÓN de desarrollo, análoga a la de publicidad y por el mismo motivo —el banner de
 * CookieYes entra por GTM y no se sirve en `localhost`, así que la cookie nunca existe y no se
 * podría probar el embudo—: `NEXT_PUBLIC_ANALYTICS_DEV_CONSENT=true` en `.env.local`. El doble
 * candado con `NODE_ENV` es a propósito: en QA y producción manda siempre la cookie real.
 */
export function hasAnalyticsConsent(): boolean {
  if (process.env.NODE_ENV === 'development' && config.tracking.analyticsDevConsent) return true;

  const consent = parseCookieYesConsent();
  return consent['analytics'] === 'yes';
}

